import React, { useState, useEffect } from "react"
import _ from 'lodash'
import { field_attrs } from "components/table/lib/column-field"

import { NumberInput, DropdownInput, CheckboxInput } from "components/table/carousel/inputs"

import YearValues from "./year-values"
import getEmptyYears, { Year } from "components/departmental-line-items/lib/get-empty-years"

import propertyService from "data/property"
import notificationService from "data/notification"

import { HorizontalHeader_s } from "components/departmental-line-items/controls/line-item-carousel-styles"

const Detailed = (props: {rowData: [], field: string, namedItems: {}, persist: Function}) => {
  const initialItems = _.get(props.rowData[props.field], "items")
  const emptyRecord = {
    lineitemdetail: {
      inflation: null
    }
  }
  const [data, setData] = useState<object|null>(props.rowData)
  const [yearData, setYearData] = useState<Year[]>(initialItems && initialItems.length ? initialItems : getEmptyYears(2, 0))
  const [years, setYears] = useState<number>(initialItems && initialItems.length ? initialItems.length : 2)
  const [yearsLimit, setYearsLimit] = useState<number>(36)
  const [none, setNone] = useState<boolean>(false)
  const inflationOptions = _.map(
    _.get(props, "namedItems.inflation"),
    item => {
      return {value: item.inflationid, label: item.code}
    }
  )
  let inflationMappedCode = "*Departmental"
  const inflationDefault = _.get(_.find(inflationOptions, o => o.label === inflationMappedCode), "value")

  useEffect(() => {
    if (!props['rowData'][props.field] || props['rowData'][props.field].state !== 'detailed') {
      setData(emptyRecord)
      setYears(2)
      setYearData(getEmptyYears(2, 0))
      setNone(true)
    }
  }, [props['rowData']])

  useEffect(() => {
    if(none === true) setNone(false)
  }, [none])

  useEffect(() => {
    let property = propertyService.get()
    if(property) {
      if(property.term && property.term >= 1 && (property.term + 2) <= 37) {
        setYearsLimit(property.term + 2)
      } else {
        setYearsLimit(37)
      }
    }
  }, [])

  function updateData(val, field: string): void {
    let localCache = _.cloneDeep(data);
    let nextData = _.cloneDeep(props.rowData)
    //clear items on first persist
    if(props['rowData'][props.field] === null || props['rowData'][props.field].state !== 'detailed') {
      _.set(nextData, "lineitemdetail.items", [])
    }
    _.set(nextData, "lineitemdetail.state", "detailed")
    _.set(nextData, "lineitemdetail.items", yearData)
    _.set(nextData, "lineitemdetail.inflation", {
      inflationMode: "namedItem",
      inflationNamedItemId: _.get(localCache, "lineitemdetail.inflation.inflationNamedItemId") || inflationDefault
    })

    if(field === "lineitemdetail.inflation.inflationNamedItemId") {
      const parentkey = "lineitemdetail.inflation"
      _.set(localCache, parentkey, {
        inflationMode: "namedItem",
        inflationNamedItemId: val
      })
      _.set(nextData, parentkey, _.get(localCache, parentkey))
    } else if (field === "lineitemdetail.items") {
      setYearData(val)
      _.set(localCache, field, val)
      _.set(nextData, field, val)
    } else {
      _.set(localCache, field, val)
      _.set(nextData, field, val)
    }
    setData(localCache)

    nextData["inputtype"] = "detailed"
    nextData["amt"] = null
    nextData["amttype"] = null
    nextData["areaid"] = null
    nextData["inflationdetail"] = null

    delete nextData.lineitemdetail.areaMeasureNamedItemId
    delete nextData.lineitemdetail.lineItemId
    delete nextData.lineitemdetail.percent
    delete nextData.lineitemdetail.revenue

    props.persist(nextData, props.field, props.rowData)
  }

  function handleYearsBlur(newYears) {
    const parsedYears = parseInt(newYears)
    if(!isNaN(parsedYears)) {
      if(parsedYears >= 1 && parsedYears <= yearsLimit) {
        changeYearDataLength(parsedYears)
      } else if (parsedYears < 1) {
        changeYearDataLength(1)
        notificationService.notify({
          type: "warn",
          message: "Years must be set to at least 1.",
          dismiss: 4000
        })
      } else if (parsedYears > yearsLimit) {
        changeYearDataLength(yearsLimit)
        notificationService.notify({
          type: "warn",
          message: `Years cannot be greater than the property term plus two years.  The limit is currently ${yearsLimit} years.`,
          dismiss: 4000
        })
      }
    } else {
      setYears(parsedYears)
    }
  }

  function changeYearDataLength(numberOfYears) {
    // add or remove year columns if the totalYears param changes
    let updatedYearData: Year[] = yearData
    if(numberOfYears > updatedYearData.length) {
      const diff = numberOfYears - updatedYearData.length
      const empties = getEmptyYears(diff, updatedYearData.length)
      updatedYearData = updatedYearData.concat(empties)
    } else if(numberOfYears < updatedYearData.length) {
      updatedYearData = updatedYearData.slice(0, numberOfYears)
    }
    setYearData(updatedYearData)
    setYears(numberOfYears)
    updateData(updatedYearData, "lineitemdetail.items")
  }

  const attrs = (field) => field_attrs('lineitem')(field)(props.rowData, props)
  const item_attrs = (field) => field_attrs('lineitem', `${props.field}.items`)(field)(props.rowData, props)

  return (
    <div>
      <HorizontalHeader_s>
        <div>
          <label>Years of Detailed Input</label>
          <NumberInput className="automated years-input" data={{years}} field="years" readPrecision={0} writePrecision={0} persist={handleYearsBlur} />
        </div>
        <div>
          <label>Inflate From Analysis Begin Date</label>
          <CheckboxInput className="automated" data={data} field="lineitemdetail.inflateFromAnalysisBegin" persist={updateData} />
        </div>
        <div>
          <label>Inflation</label>
          <DropdownInput className="automated" data={data}
            default={inflationDefault}
            field="lineitemdetail.inflation.inflationNamedItemId" options={inflationOptions} persist={updateData} attrs={attrs}/>
        </div>
      </HorizontalHeader_s>
      <YearValues
        allYearsData={yearData}
        handleUpdate={(data) => updateData(data, "lineitemdetail.items")}
        isPercent={false}
        lastFieldTabHandler={() => {}}
        shiftTabTarget={document}
        none={none}
        reset={() => {}}
        attrs={item_attrs}
        />
    </div>
  )
}

export default Detailed
